import { ApiException } from "./scanner-backend";

/**
 * RFC 7807 ProblemDetails interface
 */
interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  errors?: { [key: string]: string[] };
  error?: string; // Support for custom error field
}

/**
 * Union type for API errors
 */
export type ApiError = ApiException | Error | unknown;

/**
 * Extracts a user-friendly error message from an API error.
 * Parses RFC 7807 ProblemDetails from ApiException responses, falling back to generic messages.
 *
 * @param error - The error from an API call (typically an ApiException or Error)
 * @returns A user-friendly error message string
 */
export function getApiErrorMessage(error: ApiError): string {
  // Check for authentication errors (401 Unauthorized, 403 Forbidden)
  if (ApiException.isApiException(error)) {
    if (error.status === 401 || error.status === 403) {
      return 'Chyba autentifikácie. Prosím, prihláste sa znova.';
    }

    const hasRawResponse =
      typeof error.response === 'string' && error.response.trim().length > 0;

    // Handle ApiException with response body
    if (error.response) {
      try {
        const problemDetails: ProblemDetails = JSON.parse(error.response);

        // RFC 7807 ProblemDetails: prefer detail, then title, then error
        if (problemDetails.detail) {
          return problemDetails.detail;
        }

        if (problemDetails.title) {
          return problemDetails.title;
        }

        if (problemDetails.error) {
          return problemDetails.error;
        }

        // Handle validation errors (errors dictionary)
        if (problemDetails.errors && typeof problemDetails.errors === 'object') {
          const errorMessages: string[] = [];
          Object.values(problemDetails.errors).forEach(fieldErrors => {
            if (Array.isArray(fieldErrors)) {
              errorMessages.push(...fieldErrors);
            }
          });
          if (errorMessages.length > 0) {
            return errorMessages.join(', ');
          }
        }
      } catch {
        // If JSON parsing fails, fall back to raw response text
        if (hasRawResponse) {
          return error.response;
        }
      }
    }

    if (!hasRawResponse && error.status) {
      return `Požiadavka zlyhala (HTTP ${error.status}).`;
    }
  }

  // Fall back to error message or generic message
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Nastala neočakávaná chyba.';
}
